import { useEffect, useCallback, useState } from 'react';
import { Snackbar, Stack, Fade } from '@mui/material';
import { Alert } from '../Alert';
import { FCNC } from '../FCNC';
import { useToaster } from './useToaster';
import { IToast } from './Toaster';

type AnchorOrigin = {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
};

const DEFAULT_ANCHOR_ORIGIN: AnchorOrigin = { vertical: 'bottom', horizontal: 'left' };

export interface IToastProps {
    testId: string;
    toast: IToast;
}

const Toast: FCNC<IToastProps> = ({ testId, toast }) => {
    const [open, setOpen] = useState(true);

    const toaster = useToaster();

    const handleClose = useCallback((_: any, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    }, []);

    useEffect(() => {
        if (!toast.autoHideDuration) {
            return undefined;
        }

        const timeout = setTimeout(() => {
            handleClose(null);
        }, toast.autoHideDuration);

        return () => {
            clearTimeout(timeout);
        };
    }, [toast, handleClose]);

    return (
        <Fade
            in={open}
            timeout={400}
            onExited={() => {
                toaster.throwAwayToast(toast.id);
            }}
        >
            <div>
                <Alert testId={testId} onClose={handleClose} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </div>
        </Fade>
    );
};

export interface IToastShowerProps {
    testId: string;
}

export const ToastShower: FCNC<IToastShowerProps> = ({ testId }) => {
    const toaster = useToaster();
    const toasts = toaster.getToasts();

    // Group toasts by anchor origin
    const groupedToasts = toasts.reduce((acc, toast) => {
        const anchorOrigin = toast.anchorOrigin || DEFAULT_ANCHOR_ORIGIN;
        const key = `${anchorOrigin.vertical}-${anchorOrigin.horizontal}`;

        if (!acc[key]) {
            acc[key] = { anchorOrigin, toasts: [] };
        }

        acc[key].toasts.push(toast);
        return acc;
    }, {} as Record<string, { anchorOrigin: AnchorOrigin; toasts: IToast[] }>);

    return (
        <>
            {Object.entries(groupedToasts).map(([positionKey, { anchorOrigin, toasts: positionToasts }]) => (
                <Snackbar
                    key={positionKey}
                    data-testid={`${testId}-${positionKey}`}
                    open={positionToasts.length > 0}
                    anchorOrigin={anchorOrigin}
                >
                    <div>
                        <Stack spacing={1}>
                            {positionToasts
                                .slice(0, 3)
                                .reverse()
                                .map((toast) => (
                                    <div key={toast.id}>
                                        <Toast testId={`${testId}-toast`} toast={toast} />
                                    </div>
                                ))}
                        </Stack>
                    </div>
                </Snackbar>
            ))}
        </>
    );
};
